export { darkenHex, lightenHex, mixHex } from '~/ui/core/charts/colors';

import type { PotentialBand } from './automation';

/**
 * Exploration palettes for the potential ramp, Minimal → Very high. Every step is a token from the
 * theme ramps (gray, black, chart indigo, info, success, warning, error). Each ramp was picked by
 * searching its source ramp for the five steps with the widest, monotone lightness gaps that still
 * clear the recessed pocket surface, then checked with the dataviz ordinal validator (2026-09-04).
 * Orange stays reserved for selection, so no ramp uses it.
 */
export const PALETTES = [
    'indigo',
    'grayscale',
    'graphite',
    'blue',
    'green',
    'amber',
    'red',
    'gray-indigo',
    'gray-blue',
    'gray-green',
    'gray-amber',
    'gray-red',
    'graphite-indigo',
    'graphite-red',
    'gray-indigo-tip',
    'gray-red-tip',
    'heat',
    'blue-indigo',
    'green-blue',
    'amber-indigo',
] as const;
export type Palette = (typeof PALETTES)[number];

export const DEFAULT_PALETTE: Palette = 'graphite';

export const isPalette = (value: string | null | undefined): value is Palette => PALETTES.includes(value as Palette);

type Ramp = readonly [string, string, string, string, string];
type PaletteRamps = Record<'dark' | 'light', Ramp>;

const RAMPS: Record<Palette, PaletteRamps> = {
    indigo: {
        light: ['#a9afff', '#7c83f7', '#5f63dc', '#4447b8', '#24237a'],
        dark: ['#eef0ff', '#b7beff', '#8b93ff', '#5f63dc', '#3535a8'],
    },
    grayscale: {
        light: ['#b2b2b0', '#9a9a98', '#6e6e6c', '#505052', '#1d272d'],
        dark: ['#f9f9fc', '#c7c7c5', '#9a9a98', '#6e6e6c', '#505052'],
    },
    graphite: {
        light: ['#b1b4b9', '#80858a', '#4e565c', '#1d272d', '#040f16'],
        dark: ['#e2e3e8', '#b1b4b9', '#80858a', '#676d73', '#4e565c'],
    },
    blue: {
        light: ['#6cadf1', '#0066ff', '#003dad', '#001552', '#00001a'],
        dark: ['#f5f9ff', '#b3d4ff', '#6cadf1', '#0066ff', '#003dad'],
    },
    green: {
        light: ['#6bb572', '#359142', '#016a02', '#003a01', '#001200'],
        dark: ['#f0f9f1', '#bcdfc0', '#6bb572', '#359142', '#015201'],
    },
    amber: {
        light: ['#f79009', '#dc6803', '#b54708', '#7a2e0e', '#4e1d09'],
        dark: ['#fffcf5', '#fec84b', '#f79009', '#b54708', '#7a2e0e'],
    },
    red: {
        light: ['#fda29b', '#f04438', '#b42318', '#7a271a', '#55160c'],
        dark: ['#fffbfa', '#fda29b', '#f04438', '#b42318', '#7a271a'],
    },
    'gray-indigo': {
        light: ['#b2b2b0', '#9a9a98', '#5f63dc', '#4447b8', '#24237a'],
        dark: ['#f9f9fc', '#c7c7c5', '#8b93ff', '#5f63dc', '#3535a8'],
    },
    'gray-blue': {
        light: ['#b2b2b0', '#6e6e6c', '#003dad', '#001f6b', '#00001a'],
        dark: ['#f9f9fc', '#c7c7c5', '#6cadf1', '#0066ff', '#003dad'],
    },
    'gray-green': {
        light: ['#b2b2b0', '#6e6e6c', '#016a02', '#003a01', '#001200'],
        dark: ['#f9f9fc', '#c7c7c5', '#6bb572', '#359142', '#015201'],
    },
    'gray-amber': {
        light: ['#b2b2b0', '#9a9a98', '#b54708', '#93370d', '#4e1d09'],
        dark: ['#f9f9fc', '#c7c7c5', '#dc6803', '#b54708', '#7a2e0e'],
    },
    'gray-red': {
        light: ['#b2b2b0', '#9a9a98', '#d92d20', '#912018', '#55160c'],
        dark: ['#f9f9fc', '#c7c7c5', '#f97066', '#d92d20', '#7a271a'],
    },
    'graphite-indigo': {
        light: ['#b1b4b9', '#80858a', '#4f52d9', '#3535a8', '#24237a'],
        dark: ['#e2e3e8', '#b1b4b9', '#7c83f7', '#4f52d9', '#3535a8'],
    },
    'graphite-red': {
        light: ['#b1b4b9', '#80858a', '#b42318', '#7a271a', '#55160c'],
        dark: ['#e2e3e8', '#b1b4b9', '#f04438', '#b42318', '#7a271a'],
    },
    'gray-indigo-tip': {
        light: ['#b2b2b0', '#9a9a98', '#6e6e6c', '#505052', '#24237a'],
        dark: ['#f9f9fc', '#c7c7c5', '#9a9a98', '#6e6e6c', '#3535a8'],
    },
    'gray-red-tip': {
        light: ['#b2b2b0', '#9a9a98', '#6e6e6c', '#505052', '#55160c'],
        dark: ['#f9f9fc', '#c7c7c5', '#9a9a98', '#6e6e6c', '#7a271a'],
    },
    heat: {
        light: ['#f79009', '#dc6803', '#b42318', '#7a271a', '#55160c'],
        dark: ['#fffcf5', '#fec84b', '#f97066', '#d92d20', '#7a271a'],
    },
    'blue-indigo': {
        light: ['#a9afff', '#6c74f7', '#4447b8', '#001f6b', '#00001a'],
        dark: ['#eef0ff', '#b7beff', '#8b93ff', '#0066ff', '#003dad'],
    },
    'green-blue': {
        light: ['#6bb572', '#018b02', '#003dad', '#001552', '#00001a'],
        dark: ['#f0f9f1', '#bcdfc0', '#6cadf1', '#0066ff', '#003dad'],
    },
    'amber-indigo': {
        light: ['#f79009', '#dc6803', '#4f52d9', '#3535a8', '#24237a'],
        dark: ['#fffcf5', '#fec84b', '#8b93ff', '#5f63dc', '#3535a8'],
    },
};

const BAND_STEP: Record<PotentialBand, 0 | 1 | 2 | 3 | 4> = {
    HIGH: 3,
    LOW: 1,
    MODERATE: 2,
    NONE: 0,
    VERY_HIGH: 4,
};

export const paletteBandColor = (palette: Palette, colorScheme: 'dark' | 'light', band: PotentialBand): string =>
    RAMPS[palette][colorScheme][BAND_STEP[band]];

const LIGHT_INK = '#ffffff';
const DARK_INK = '#040f16';

const channel = (hex: string, offset: number): number => {
    const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;

    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (hex: string): number =>
    0.2126 * channel(hex, 1) + 0.7152 * channel(hex, 3) + 0.0722 * channel(hex, 5);

/** Relative luminance where white ink stops out-contrasting near-black ink. */
const INK_CROSSOVER = 0.179;

/** Label ink for text drawn on a band fill: light ink once the fill is dark enough to carry it. */
export const inkOnFill = (hex: string): string => (relativeLuminance(hex) < INK_CROSSOVER ? LIGHT_INK : DARK_INK);
